import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';

import { accountabilityAPI } from '@/services/accountabilityAPI';
import {
  WorkerSuspension,
  SuspensionFilters,
  Pagination,
} from '@/types/accountability';

import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';

import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  SelectChangeEvent,
} from '@mui/material';

import Alert from '@/components/Alert';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import TextStack from '@/components/TextStack';
import { Body, Small } from '@/components/Typography';
import Button from '@/components/Button';
import { TableCell, TableRow } from '@/elements/Table';
import Modal from '@/components/Modal';
import TextArea from '@/components/TextArea';
import styled from '@/styles';

const headerFields = [
  'Worker',
  'Type',
  'Strike #',
  'Duration',
  'Start Date',
  'End Date',
  'Status',
  '',
];

const TabBar = styled('div', {
  display: 'inline-flex',
  backgroundColor: 'white',
  borderRadius: '$lg $lg 0 0',
  padding: '0 4px',
  borderBottom: '1px solid $colors$neutralLightest',
});

const TabLabel = styled('span', {
  display: 'inline-block',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: 500,
  color: '$colors$textDefault',
  borderBottom: '2px solid $colors$themeDefault',
});

type LiftModalProps = {
  suspension: WorkerSuspension;
  onClose: () => void;
  onSuccess: () => void;
};

const LiftModal = ({ suspension, onClose, onSuccess }: LiftModalProps) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!reason.trim()) return;

    setLoading(true);
    setError(null);

    try {
      await accountabilityAPI.liftSuspension(suspension.id, reason);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to lift suspension';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Lift Suspension"
      onRequestClose={onClose}
    >
      <Card.Section>
        <Body>
          Lift suspension for <strong>{suspension.worker_name}</strong>? This
          will restore their access to the platform.
        </Body>
      </Card.Section>
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon={faExclamationTriangle}
            status="danger"
            title="Error"
          />
        )}
        <FormElement label="Reason for lifting">
          <TextArea
            placeholder="Provide a reason for lifting this suspension."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormElement>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Lift Suspension"
            isLoading={loading}
            label="Lift Suspension"
            status="success"
            onClick={handleSubmit}
            disabled={!reason.trim()}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export const SuspensionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [suspensions, setSuspensions] = useState<WorkerSuspension[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');

  const [filters, setFilters] = useState<SuspensionFilters>({
    page: parseInt(searchParams.get('page') || '1'),
    per_page: parseInt(searchParams.get('per_page') || '25'),
    status:
      (searchParams.get('status') as SuspensionFilters['status']) || undefined,
    suspension_type:
      (searchParams.get('suspension_type') as 'late_drop' | 'no_show') ||
      undefined,
  });

  const [liftModal, setLiftModal] = useState<{
    open: boolean;
    suspension: WorkerSuspension;
  } | null>(null);

  const updateSearchParams = useCallback(
    (newFilters: SuspensionFilters) => {
      const params = new URLSearchParams();
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const loadSuspensions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountabilityAPI.getSuspensions(filters);

      if (response.success) {
        setSuspensions(
          (response.suspensions || response.data || []) as WorkerSuspension[],
        );
        setPagination(response.pagination || null);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load suspensions';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadSuspensions();
  }, [loadSuspensions]);

  const handleFilterChange = (newFilters: Partial<SuspensionFilters>) => {
    const updated = { ...filters, ...newFilters, page: 1 };
    setFilters(updated);
    updateSearchParams(updated);
  };

  const handleSearch = () => {
    handleFilterChange({ search: searchInput || undefined });
  };

  const handlePageChange = (pageNumber: number) => {
    const updated = { ...filters, page: pageNumber };
    setFilters(updated);
    updateSearchParams(updated);
  };

  const handleNumberItemsChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const updated = {
      ...filters,
      page: 1,
      per_page: parseInt(event.target.value),
    };
    setFilters(updated);
    updateSearchParams(updated);
  };

  const handleLiftSuccess = () => {
    setSuccess('Suspension lifted successfully');
    loadSuspensions();
    setLiftModal(null);
  };

  const getStatusBadge = (status: WorkerSuspension['status']) => {
    const statusMap: Record<
      WorkerSuspension['status'],
      { label: string; status: 'danger' | 'warning' | 'success' | 'neutral' }
    > = {
      active: { label: 'Active', status: 'danger' },
      pending: { label: 'Pending', status: 'warning' },
      completed: { label: 'Completed', status: 'success' },
      lifted: { label: 'Lifted', status: 'neutral' },
    };
    const config = statusMap[status] || { label: status, status: 'neutral' };
    return <Badge label={config.label} status={config.status} />;
  };

  const getSuspensionTypeBadge = (type: string) => {
    return type === 'late_drop' ? (
      <Badge label="Late Drop" status="warning" />
    ) : (
      <Badge label="No Show" status="danger" />
    );
  };

  const totalItems = pagination?.total_count || 0;

  const hasActiveFilters =
    searchInput || filters.status || filters.suspension_type;

  return (
    <Page
      footer={
        suspensions.length > 0 &&
        pagination && (
          <Paginator
            currentPage={filters.page || 1}
            handleNumberItemsChange={handleNumberItemsChange}
            handlePageChange={handlePageChange}
            itemsLength={totalItems}
            itemsPerPage={filters.per_page || 25}
          />
        )
      }
      size="full"
      title="Tasks"
    >
      {liftModal && (
        <LiftModal
          suspension={liftModal.suspension}
          onClose={() => setLiftModal(null)}
          onSuccess={handleLiftSuccess}
        />
      )}

      <TabBar>
        <TabLabel>Violations</TabLabel>
      </TabBar>

      {(error || success) && (
        <Card>
          <Card.Section>
            <Stack vertical gap={8}>
              {error && (
                <Alert
                  description={error}
                  icon={faExclamationTriangle}
                  status="danger"
                  title="Error"
                />
              )}
              {success && (
                <Alert
                  description={success}
                  icon={faCheckCircle}
                  status="success"
                  title="Success"
                />
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Stack gap={16} style={{ flexWrap: 'wrap' }}>
              <TextField
                label="Search"
                placeholder="Search by worker name..."
                size="small"
                sx={{ minWidth: 250, flex: 1 }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status || ''}
                  onChange={(e: SelectChangeEvent) =>
                    handleFilterChange({
                      status:
                        (e.target.value as SuspensionFilters['status']) ||
                        undefined,
                    })
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="lifted">Lifted</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Suspension Type</InputLabel>
                <Select
                  label="Suspension Type"
                  value={filters.suspension_type || ''}
                  onChange={(e: SelectChangeEvent) =>
                    handleFilterChange({
                      suspension_type:
                        (e.target.value as 'late_drop' | 'no_show') ||
                        undefined,
                    })
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="late_drop">Late Drop</MenuItem>
                  <MenuItem value="no_show">No Show</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {hasActiveFilters && (
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <Small color="lighter">Active filters:</Small>
                {searchInput && (
                  <Chip
                    label={`Search: ${searchInput}`}
                    size="small"
                    onDelete={() => {
                      setSearchInput('');
                      handleFilterChange({ search: undefined });
                    }}
                  />
                )}
                {filters.status && (
                  <Chip
                    label={`Status: ${filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}`}
                    size="small"
                    onDelete={() => handleFilterChange({ status: undefined })}
                  />
                )}
                {filters.suspension_type && (
                  <Chip
                    label={`Type: ${filters.suspension_type === 'late_drop' ? 'Late Drop' : 'No Show'}`}
                    size="small"
                    onDelete={() =>
                      handleFilterChange({ suspension_type: undefined })
                    }
                  />
                )}
                <Chip
                  color="primary"
                  label="Clear all"
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setFilters({ page: 1, per_page: 25 });
                    setSearchInput('');
                    setSearchParams(new URLSearchParams());
                  }}
                />
              </Stack>
            )}
          </Stack>
        </Card.Section>
      </Card>

      <Card>
        <Table>
          <TableHeader fields={headerFields} />
          <tbody>
            {suspensions.map((suspension) => (
              <TableRow key={suspension.id}>
                <TableCell>
                  <Body weight="medium">{suspension.worker_name}</Body>
                </TableCell>
                <TableCell>
                  {getSuspensionTypeBadge(suspension.suspension_type)}
                </TableCell>
                <TableCell>
                  <Body>{suspension.strike_number}</Body>
                </TableCell>
                <TableCell>
                  <Body>
                    {suspension.duration_days === 0
                      ? 'Permanent'
                      : `${suspension.duration_days} days`}
                  </Body>
                </TableCell>
                <TableCell>
                  <TextStack spacing="tight">
                    <Body weight="medium">
                      {format(new Date(suspension.starts_at), 'MM/dd/yyyy')}
                    </Body>
                    <Body size="sm" color="light">
                      {format(new Date(suspension.starts_at), 'h:mm a')}
                    </Body>
                  </TextStack>
                </TableCell>
                <TableCell>
                  {suspension.ends_at ? (
                    <TextStack spacing="tight">
                      <Body weight="medium">
                        {format(new Date(suspension.ends_at), 'MM/dd/yyyy')}
                      </Body>
                      <Body size="sm" color="light">
                        {format(new Date(suspension.ends_at), 'h:mm a')}
                      </Body>
                    </TextStack>
                  ) : (
                    <Badge label="Permanent" status="danger" />
                  )}
                </TableCell>
                <TableCell>{getStatusBadge(suspension.status)}</TableCell>
                <TableCell>
                  {(suspension.status === 'active' ||
                    suspension.status === 'pending') && (
                    <Button
                      a11yLabel="Lift Suspension"
                      appearance="outline"
                      label="Lift"
                      status="success"
                      onClick={() => setLiftModal({ open: true, suspension })}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <LoadingState overlayColor="white" />
                </TableCell>
              </TableRow>
            )}
            {!loading && suspensions.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={headerFields.length}
                  style={{ textAlign: 'center' }}
                >
                  <Stack vertical gap={8} align="center">
                    <Body>No suspensions found.</Body>
                    <Button
                      a11yLabel="Clear filters"
                      appearance="outline"
                      label="Clear Filters"
                      onClick={() => {
                        setFilters({ page: 1, per_page: 25 });
                        setSearchInput('');
                        setSearchParams(new URLSearchParams());
                      }}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </Card>
    </Page>
  );
};

export default SuspensionsPage;
